import { ThemeProvider } from '@etg/wings';
import { AuthApolloProvider } from '@eti/authentication';
import {
  DirectionProvider,
  DIRECTIONS,
  PropertiesProvider,
  SearchProvider,
  TranslationProvider,
} from '@eti/providers';
import { price } from '@eti/utils';
import { domMax, LazyMotion } from 'framer-motion';
import { Provider } from 'react-redux';
import { BrowserRouter as Router } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { generateTextResolverConfig } from '../utils/textResolverConfigUtil';
import { createStore } from '../utils/storeUtil';
import MainContainer from '../containers/MainContainer';
import ErrorBoundary from '../../infrastructure/error-boundaries/components/ErrorBoundary';
import getBrandCode from '../../utils/brandCode';
import { setInitialSearchContextData } from '../utils/searchProviderDataUtil';
import SubSessionUrlSync from '../../subSession/SubSessionUrlSync';
import { MULTI_SESSION_ENABLED_PROPERTY } from '../../subSession/wireContract';
import { getSubSessionHeaders } from '../../common/fetch/subSessionHeaders';

declare const window: Window & { spa_variables: any };

// TODO: Pass correct format to `PriceFormatter`
price.setPriceFormatter(price.createPriceFormatter(window.spa_variables.priceFormat));

const App = () => {
  const { debugEnabled, properties, texts, isRTL, searchParameters, siteContext } =
    window.spa_variables;
  const store = createStore(debugEnabled);
  const translationsConfigOptions = generateTextResolverConfig(debugEnabled);
  const direction = isRTL ? DIRECTIONS.RTL : DIRECTIONS.LTR;
  const shouldUseDarkMode = properties['CustomerWeb.UseDarkModeTheme.Enabled'];
  const shouldUsePartnerTheme = properties['IbeClient.UsePartnerTheme.Enabled'];
  const isMultiSessionEnabled = Boolean(properties[MULTI_SESSION_ENABLED_PROPERTY]);
  const brandCode = getBrandCode(
    { brand: siteContext.brand, partner: siteContext.partner },
    shouldUsePartnerTheme,
  );
  const searchData = setInitialSearchContextData(
    searchParameters,
    properties['Result.QuickSort.Order'],
  );

  return (
    <HelmetProvider>
      <Router basename={window.spa_variables.basePath}>
        <LazyMotion features={domMax} strict>
          {/* TODO: WEBTECH-1545 (investigation) - options is stored in context but never
              read by useTranslation, so this shape mismatch may be purely cosmetic. */}
          <TranslationProvider
            options={translationsConfigOptions as { clearCache?: boolean }}
            translations={texts}
          >
            <DirectionProvider direction={direction}>
              <AuthApolloProvider brandCode={brandCode} getExtraHeaders={getSubSessionHeaders}>
                <ThemeProvider brand={brandCode} shouldSupportDarkMode={shouldUseDarkMode}>
                  <SearchProvider searchData={searchData}>
                    <Provider store={store}>
                      <PropertiesProvider properties={properties}>
                        <ErrorBoundary>
                          {isMultiSessionEnabled && <SubSessionUrlSync />}
                          <MainContainer />
                        </ErrorBoundary>
                      </PropertiesProvider>
                    </Provider>
                  </SearchProvider>
                </ThemeProvider>
              </AuthApolloProvider>
            </DirectionProvider>
          </TranslationProvider>
        </LazyMotion>
      </Router>
    </HelmetProvider>
  );
};

export default App;
